import { useCallback, useEffect, useMemo, useRef } from 'react';

import { EWebTreatmentRequestLog, TreatmentRequestLog } from '@/entities/diagnosis/libs';
import useLogger from '@/hooks/useLogger';
import { NonFaceToFaceBookingSchedulesInfo, TreatmentRequest, TreatmentRequestInflowType } from '@/models/diagnosis';
import { AccordionState } from './useTelemedicineRequestViewModel';

interface UseTelemedicineScheduleParams {
  treatmentRequest: TreatmentRequest;
  bookingSchedulesInfo: NonFaceToFaceBookingSchedulesInfo;
  updateFormFromTreatmentRequest: (request: TreatmentRequest) => void;
  accordionRefs: Record<
    AccordionState,
    React.RefObject<{ openSection: (options?: { scroll?: boolean }) => void; closeSection: () => void }>
  >;
  focusSsnInput: () => Promise<void>;
}

export const useTelemedicineSchedule = ({
  treatmentRequest,
  bookingSchedulesInfo,
  updateFormFromTreatmentRequest,
  accordionRefs,
  focusSsnInput,
}: UseTelemedicineScheduleParams) => {
  const logger = useLogger();
  const treatmentRequestRef = useRef(treatmentRequest);
  const updateFormFromTreatmentRequestRef = useRef(updateFormFromTreatmentRequest);

  useEffect(() => {
    treatmentRequestRef.current = treatmentRequest;
  }, [treatmentRequest]);

  useEffect(() => {
    updateFormFromTreatmentRequestRef.current = updateFormFromTreatmentRequest;
  }, [updateFormFromTreatmentRequest]);

  const selectedDay = useMemo(() => {
    return bookingSchedulesInfo.findSelectedDayScheduleGroupedByTimePeriod(treatmentRequest);
  }, [bookingSchedulesInfo, treatmentRequest]);

  const selectedDayScheduleLength = useMemo(() => {
    return (
      (selectedDay?.dawnSchedules.length ?? 0) +
      (selectedDay?.morningSchedules.length ?? 0) +
      (selectedDay?.afternoonSchedules.length ?? 0) +
      (selectedDay?.nightSchedules.length ?? 0)
    );
  }, [selectedDay]);

  const selectBookingTargetDate = useCallback((bookingTargetDate: string) => {
    const currentRequest = treatmentRequestRef.current;
    currentRequest.selectBookingTargetDate(bookingTargetDate);
    updateFormFromTreatmentRequestRef.current(currentRequest);
  }, []);

  const selectBookingTimeSlot = useCallback(
    async (startTimeSlotValue: number, endTimeSlotValue: number) => {
      const currentRequest = treatmentRequestRef.current;
      currentRequest.selectBookingTimeSlot(startTimeSlotValue, endTimeSlotValue);
      updateFormFromTreatmentRequestRef.current(currentRequest);

      logger.log({
        eventName: EWebTreatmentRequestLog.ClickRequestTime,
      });

      if (currentRequest.isInHospitalDelivery()) {
        accordionRefs[AccordionState.Days].current?.closeSection();
        return;
      }

      accordionRefs[AccordionState.Days].current?.closeSection();
      accordionRefs[AccordionState.Ssn].current?.openSection({ scroll: true });

      await focusSsnInput();
    },
    [accordionRefs, focusSsnInput, logger]
  );

  const toggleAsapRequest = useCallback(() => {
    const currentRequest = treatmentRequestRef.current;
    currentRequest.toggleAsapRequest();
    updateFormFromTreatmentRequestRef.current(currentRequest);
  }, []);

  const selectQuickMode = useCallback(async () => {
    const currentRequest = treatmentRequestRef.current;
    currentRequest.setScheduleMode(TreatmentRequestInflowType.WEB_QUICK);
    updateFormFromTreatmentRequestRef.current(currentRequest);

    accordionRefs[AccordionState.Days].current?.closeSection();
    accordionRefs[AccordionState.Ssn].current?.openSection({ scroll: false });
    await focusSsnInput();
  }, [accordionRefs, focusSsnInput]);

  const selectGeneralMode = useCallback(() => {
    const currentRequest = treatmentRequestRef.current;
    currentRequest.setScheduleMode(TreatmentRequestInflowType.WEB_GENERAL);

    const firstDay = bookingSchedulesInfo.daySchedulesGroupedByTimePeriod[0];
    if (firstDay) {
      currentRequest.selectBookingTargetDate(firstDay.targetDate);
    }

    updateFormFromTreatmentRequestRef.current(currentRequest);

    accordionRefs[AccordionState.Days].current?.openSection();
  }, [accordionRefs, bookingSchedulesInfo]);

  const toggleQuickScheduleMode = useCallback(async () => {
    const currentRequest = treatmentRequestRef.current;
    currentRequest.toggleQuickScheduleMode();
    updateFormFromTreatmentRequestRef.current(currentRequest);

    if (!currentRequest.isWebQuickScheduleRequest()) {
      accordionRefs[AccordionState.Days].current?.openSection();
    } else {
      accordionRefs[AccordionState.Days].current?.closeSection();
      accordionRefs[AccordionState.Ssn].current?.openSection({ scroll: false });
      await focusSsnInput();
    }
  }, [accordionRefs, focusSsnInput]);

  const selectInitSchedule = useCallback(
    async (targetDate: string) => {
      const currentRequest = treatmentRequestRef.current;
      const currentSelectedDate = currentRequest.form.bookingTargetDate;

      if (currentRequest.isWebQuickScheduleRequest()) {
        accordionRefs[AccordionState.Days].current?.openSection();
        return;
      }

      if (!targetDate) {
        logger.log({
          eventName: TreatmentRequestLog.RequestPageInWithoutTargetDate,
        });

        accordionRefs[AccordionState.Days].current?.openSection();
        return;
      }

      logger.log({
        eventName: TreatmentRequestLog.RequestPageInWithTargetDate,
      });

      if (targetDate !== currentSelectedDate) {
        currentRequest.selectBookingTargetDate(targetDate);
        updateFormFromTreatmentRequestRef.current(currentRequest);
      }

      accordionRefs[AccordionState.Ssn].current?.openSection();
      await focusSsnInput();
    },
    [accordionRefs, focusSsnInput, logger]
  );

  return {
    selectedDay,
    selectedDayScheduleLength,
    selectBookingTargetDate,
    selectBookingTimeSlot,
    toggleAsapRequest,
    selectQuickMode,
    selectGeneralMode,
    toggleQuickScheduleMode,
    selectInitSchedule,
  };
};
